import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { tenantsAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import { Building2, Plus, Search, Users, Eye, Pause, Play } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const AVAILABLE_MODULES = [
  { key: 'timesheet_ocr', label: 'Timesheets' },
  { key: 'accounting_intake', label: 'Accounting Intake' },
  { key: 'contracts', label: 'Contracts' },
  { key: 'accounting_repository', label: 'Accounting Repository' },
  { key: 'document_submission', label: 'Document Submission' },
];

const PlatformTenantsPage = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', slug: '', description: '', admin_email: '', enabled_modules: [] });

  useEffect(() => { loadTenants(); }, []);

  const loadTenants = async () => {
    try {
      const res = await tenantsAPI.list();
      setTenants(res.data);
    } catch (e) {
      toast.error('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const toggleModule = (key) => {
    setForm((f) => ({
      ...f,
      enabled_modules: f.enabled_modules.includes(key)
        ? f.enabled_modules.filter((m) => m !== key)
        : [...f.enabled_modules, key],
    }));
  };

  const handleCreate = async () => {
    if (!form.name.trim() || !form.slug.trim()) { toast.error('Name and slug are required'); return; }
    try {
      await tenantsAPI.create(form);
      toast.success('Tenant created');
      setDialogOpen(false);
      setForm({ name: '', slug: '', description: '', admin_email: '', enabled_modules: [] });
      loadTenants();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create tenant');
    }
  };

  const handleSuspend = async (tenantId) => {
    try {
      await tenantsAPI.suspend(tenantId);
      toast.success('Tenant suspended');
      loadTenants();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to suspend');
    }
  };

  const handleReactivate = async (tenantId) => {
    try {
      await tenantsAPI.reactivate(tenantId);
      toast.success('Tenant reactivated');
      loadTenants();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reactivate');
    }
  };

  const filtered = tenants.filter((t) =>
    t.name.toLowerCase().includes(search.toLowerCase()) || t.slug.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="platform-tenants-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 flex items-center gap-3">
            <Building2 className="h-8 w-8 text-indigo-600" /> Tenants
          </h1>
          <p className="text-slate-500 mt-1">Manage organizations on the platform</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-tenant-btn"><Plus className="mr-2 h-4 w-4" />Create Tenant</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create New Tenant</DialogTitle>
              <DialogDescription>Set up a new organization on the platform</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Organization Name *</Label>
                <Input
                  placeholder="Acme Corporation"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value, slug: generateSlug(e.target.value) }))}
                  data-testid="new-tenant-name"
                />
              </div>
              <div className="space-y-2">
                <Label>URL Slug *</Label>
                <Input placeholder="acme-corp" value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} data-testid="new-tenant-slug" />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input placeholder="Brief description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Admin Email</Label>
                <Input type="email" placeholder="admin@acme.com" value={form.admin_email} onChange={(e) => setForm((f) => ({ ...f, admin_email: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Enabled Modules</Label>
                <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
                  {AVAILABLE_MODULES.map((m) => (
                    <div key={m.key} className="flex items-center gap-2">
                      <Checkbox
                        id={`mod-${m.key}`}
                        checked={form.enabled_modules.includes(m.key)}
                        onCheckedChange={() => toggleModule(m.key)}
                      />
                      <label htmlFor={`mod-${m.key}`} className="text-sm">{m.label}</label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleCreate} data-testid="save-tenant-btn">Create Tenant</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Search tenants..." className="pl-10" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="search-tenants" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900">No tenants found</h3>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-semibold uppercase">Organization</TableHead>
                  <TableHead className="text-xs font-semibold uppercase">Modules</TableHead>
                  <TableHead className="text-xs font-semibold uppercase">Status</TableHead>
                  <TableHead className="text-xs font-semibold uppercase">Created</TableHead>
                  <TableHead className="text-xs font-semibold uppercase">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((tenant) => (
                  <TableRow key={tenant.id} className="hover:bg-slate-50/80" data-testid={`tenant-row-${tenant.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-50 rounded-lg">
                          <Building2 className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{tenant.name}</p>
                          <p className="text-sm text-slate-500 font-mono">{tenant.slug}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {tenant.enabled_modules?.map((m) => (
                          <Badge key={m} variant="outline" className="text-xs">{m.replace(/_/g, ' ')}</Badge>
                        ))}
                        {(!tenant.enabled_modules || tenant.enabled_modules.length === 0) && (
                          <span className="text-xs text-slate-400">None</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={tenant.is_active ? 'bg-emerald-100 text-emerald-700 border-0' : 'bg-red-100 text-red-700 border-0'}>
                        {tenant.is_active ? 'Active' : 'Suspended'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {format(new Date(tenant.created_at), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/platform/tenants/${tenant.id}`)} data-testid={`view-tenant-${tenant.id}`}>
                          <Eye className="mr-1 h-4 w-4" /> Manage
                        </Button>
                        {tenant.is_active ? (
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleSuspend(tenant.id)} data-testid={`suspend-tenant-${tenant.id}`}>
                            <Pause className="mr-1 h-4 w-4" /> Suspend
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm" className="text-emerald-600" onClick={() => handleReactivate(tenant.id)} data-testid={`reactivate-tenant-${tenant.id}`}>
                            <Play className="mr-1 h-4 w-4" /> Reactivate
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PlatformTenantsPage;
